import React, { useState } from 'react';
import { Search, Globe, Server, Shield, Cloud, FileText, Link, AlertCircle, RefreshCw, ExternalLink, Clock, Mail, CheckCircle, XCircle, Info, Image, Hash, FileSearch, Users as UsersIcon, MessageSquare, BarChart3, Sparkles, Copy, Check, Settings } from 'lucide-react';

interface DnsInfo {
  aRecords?: string[];
  aaaaRecords?: string[];
  mxRecords?: { priority: number; exchange: string }[];
  txtRecords?: string[];
  cnameRecord?: string;
  nsRecords?: string[];
}

interface SslInfo {
  issuer?: string;
  validFrom?: string;
  validTo?: string;
  isValid?: boolean;
  daysRemaining?: number;
}

interface WebsiteMetadata {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  favicon?: string;
  language?: string;
  viewport?: string;
  robots?: string;
  author?: string;
  generator?: string;
}

interface TechnologyInfo {
  cms?: string;
  webServer?: string;
  jsFrameworks?: string[];
  analytics?: string[];
  cdns?: string[];
  hosting?: string;
}

interface ContentInfo {
  headings: Record<string, string[]>;
  images: Array<{ src: string; alt: string }>;
  links: {
    internal: Array<{ href: string; text: string }>;
    external: Array<{ href: string; text: string }>;
  };
  forms: Array<{
    action: string;
    method: string;
    inputs: Array<{ type: string; name: string; placeholder: string }>;
  }>;
  contactInfo: {
    emails?: string[];
    phones?: string[];
  };
  socialMedia: Array<{
    platform: string;
    links: string[];
  }>;
  structuredData: any[];
  textContent: {
    paragraphs: number;
    wordCount: number;
    mainContent: string;
  };
}

interface DnsRecommendation {
  type: string;
  name: string;
  value: string;
  ttl?: number;
  priority?: number;
  explanation: string;
}

interface WebsiteSummary {
  businessType: string;
  industry: string;
  summary: string;
  targetAudience: string;
  keyFeatures: string[];
  servicesOrProducts: string[];
  businessModel: string;
}

interface DomainInfo {
  domain: string;
  isReachable: boolean;
  responseTime?: number;
  statusCode?: number;
  ipAddress?: string;
  dns?: DnsInfo;
  ssl?: SslInfo;
  metadata?: WebsiteMetadata;
  technologies?: TechnologyInfo;
  headers?: Record<string, string>;
  content?: ContentInfo;
  dnsRecommendations?: DnsRecommendation[];
  websiteSummary?: WebsiteSummary;
  error?: string;
  timestamp?: string;
  finalUrl?: string;
}

export const DnsDemo: React.FC = () => {
  const [domainInput, setDomainInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [domainInfo, setDomainInfo] = useState<DomainInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'dns' | 'metadata' | 'tech' | 'headers' | 'content'>('overview');
  const [copiedRecord, setCopiedRecord] = useState<number | null>(null);
  const [loadingStep, setLoadingStep] = useState(0);

  const loadingSteps = [
    { icon: Globe, text: 'Connecting to domain...', color: 'blue' },
    { icon: FileSearch, text: 'Analyzing website content...', color: 'purple' },
    { icon: Server, text: 'Checking DNS records...', color: 'green' },
    { icon: Shield, text: 'Verifying SSL certificate...', color: 'yellow' },
    { icon: Cloud, text: 'Detecting technologies...', color: 'indigo' },
    { icon: Sparkles, text: 'AI generating recommendations...', color: 'pink' },
  ];

  const extractDomain = (input: string): string => {
    // Remove protocol if present
    let domain = input.replace(/^https?:\/\//, '');
    // Remove www. if present
    domain = domain.replace(/^www\./, '');
    // Remove path and query params
    domain = domain.split('/')[0].split('?')[0];
    // Remove port if present
    domain = domain.split(':')[0];
    return domain.toLowerCase().trim();
  };

  const handleFetchDomainInfo = async () => {
    const domain = extractDomain(domainInput);
    
    if (!domain) {
      setError('Please enter a valid domain name');
      return;
    }

    setIsLoading(true);
    setError(null);
    setDomainInfo(null);
    setLoadingStep(0);

    // Start the loading animation
    const stepInterval = setInterval(() => {
      setLoadingStep((prev) => {
        if (prev < loadingSteps.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, 800);

    try {
      const response = await fetch('/api/dns-demo/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_API_KEY || ''
        },
        body: JSON.stringify({ domain })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setDomainInfo(data.info);
        setActiveTab('overview');
      } else {
        setError(data.error || 'Failed to fetch domain information');
      }
    } catch (err) {
      console.error('Error fetching domain info:', err);
      setError('Failed to connect to the server. Please try again.');
    } finally {
      clearInterval(stepInterval);
      setIsLoading(false);
      setLoadingStep(0);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isLoading) {
      handleFetchDomainInfo();
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getDaysUntilExpiry = (validTo?: string) => {
    if (!validTo) return null;
    const expiryDate = new Date(validTo);
    const today = new Date();
    const diffTime = expiryDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getSslStatusColor = (daysRemaining?: number) => {
    if (!daysRemaining) return 'text-gray-500';
    if (daysRemaining < 0) return 'text-red-600';
    if (daysRemaining < 30) return 'text-yellow-600';
    return 'text-green-600';
  };

  const copyDnsRecord = async (value: string, index: number) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedRecord(index);
      setTimeout(() => setCopiedRecord(null), 2000);
    } catch (err) {
      console.error('Failed to copy DNS record:', err);
    }
  };

  const renderDnsRecommendations = () => {
    if (!domainInfo?.dnsRecommendations || domainInfo.dnsRecommendations.length === 0) {
      return (
        <div className="text-center py-8">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No DNS recommendations available</p>
          <p className="text-sm text-gray-400 mt-1">Make sure you have configured your OpenAI API key</p>
        </div>
      );
    }

    const { dnsRecommendations } = domainInfo;

    // Helper function to determine record category
    const getRecordCategory = (record: DnsRecommendation) => {
      // Email authentication
      if (record.type === 'TXT') {
        if (record.name === '@' && record.value?.startsWith('v=spf1')) return 'email-auth';
        if (record.name?.includes('._domainkey')) return 'email-auth';
        if (record.name === '_dmarc') return 'email-auth';
      }
      
      // Email routing
      if (record.type === 'MX') return 'email-routing';
      
      // Subdomains
      if (record.type === 'CNAME' && record.name !== '@') return 'subdomains';
      
      // Root domain
      if ((record.type === 'A' || record.type === 'AAAA') && record.name === '@') return 'root';
      
      // Security
      if (record.type === 'CAA') return 'security';
      
      // Advanced
      if (record.type === 'SRV' || (record.type === 'TXT' && record.name?.startsWith('_'))) return 'advanced';
      
      return 'other';
    };

    // Group records by category
    const groupedRecords = dnsRecommendations.reduce((acc, record, index) => {
      const category = getRecordCategory(record);
      if (!acc[category]) acc[category] = [];
      acc[category].push({ ...record, originalIndex: index });
      return acc;
    }, {} as Record<string, Array<DnsRecommendation & { originalIndex: number }>>);

    const categoryInfo = {
      'email-auth': { 
        title: 'Email Authentication', 
        icon: Mail, 
        color: 'blue',
        description: 'SPF, DKIM, and DMARC records for email security'
      },
      'email-routing': { 
        title: 'Email Routing', 
        icon: Mail, 
        color: 'indigo',
        description: 'MX records for email delivery'
      },
      'subdomains': { 
        title: 'Subdomains & Services', 
        icon: Globe, 
        color: 'purple',
        description: 'CNAME records for various services and subdomains'
      },
      'root': { 
        title: 'Root Domain', 
        icon: Server, 
        color: 'green',
        description: 'A and AAAA records for the main domain'
      },
      'security': { 
        title: 'Security', 
        icon: Shield, 
        color: 'red',
        description: 'CAA and other security-related records'
      },
      'advanced': { 
        title: 'Advanced Configuration', 
        icon: Settings, 
        color: 'orange',
        description: 'SRV and special TXT records'
      },
      'other': { 
        title: 'Additional Records', 
        icon: FileText, 
        color: 'gray',
        description: 'Other recommended records'
      }
    };

    return (
      <div className="space-y-8">
        {Object.entries(groupedRecords).map(([category, records]) => {
          const info = categoryInfo[category as keyof typeof categoryInfo];
          const Icon = info?.icon || FileText;
          
          return (
            <div key={category}>
              <div className="flex items-center space-x-3 mb-4">
                <div className={`p-2 rounded-lg bg-${info?.color || 'gray'}-100 dark:bg-${info?.color || 'gray'}-900/30`}>
                  <Icon className={`h-5 w-5 text-${info?.color || 'gray'}-600 dark:text-${info?.color || 'gray'}-400`} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{info?.title || 'Records'}</h3>
                  <p className="text-sm text-gray-500">{info?.description}</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {records.map((record) => {
                  const isEmailAuth = category === 'email-auth';
                  const recordType = isEmailAuth ? 
                    (record.value?.startsWith('v=spf1') ? 'SPF' : 
                     record.name?.includes('._domainkey') ? 'DKIM' : 
                     record.name === '_dmarc' ? 'DMARC' : record.type) 
                    : record.type;
                  
                  return (
                    <div 
                      key={record.originalIndex} 
                      className="bg-white dark:bg-gray-800 p-5 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className={`text-xs font-semibold uppercase px-2 py-0.5 rounded bg-${info?.color || 'gray'}-100 dark:bg-${info?.color || 'gray'}-900 text-${info?.color || 'gray'}-700 dark:text-${info?.color || 'gray'}-300`}>
                              {recordType}
                            </span>
                            <span className="text-sm text-gray-500">TTL: {record.ttl || 3600}s</span>
                          </div>
                          <h4 className="font-mono text-sm font-medium">{record.name}</h4>
                        </div>
                        <button
                          onClick={() => copyDnsRecord(record.value, record.originalIndex)}
                          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                          title="Copy value"
                        >
                          {copiedRecord === record.originalIndex ? (
                            <Check className="h-4 w-4 text-green-500" />
                          ) : (
                            <Copy className="h-4 w-4 text-gray-500" />
                          )}
                        </button>
                      </div>
                      
                      {record.priority !== undefined && (
                        <div className="mb-2">
                          <span className="text-xs text-gray-600 dark:text-gray-400">Priority:</span>
                          <span className="ml-2 font-mono text-xs">{record.priority}</span>
                        </div>
                      )}
                      
                      <div className="mb-3">
                        <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Value:</div>
                        <div className="p-2.5 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700">
                          <code className="text-xs break-all text-gray-700 dark:text-gray-300">{record.value}</code>
                        </div>
                      </div>
                      
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        <span className="font-medium">Purpose:</span> {record.explanation}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderOverviewTab = () => {
    if (!domainInfo) return null;

    return (
      <div className="space-y-6">
        {/* Basic Info */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-2">
              <Globe className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Status</span>
            </div>
            <div className="flex items-center space-x-2">
              {domainInfo.isReachable ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              <span className={`font-semibold ${domainInfo.isReachable ? 'text-green-600' : 'text-red-600'}`}>
                {domainInfo.isReachable ? 'Online' : 'Offline'}
              </span>
            </div>
            {domainInfo.statusCode && (
              <p className="text-xs text-gray-500 mt-1">HTTP {domainInfo.statusCode}</p>
            )}
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-2">
              <Clock className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Response Time</span>
            </div>
            <p className="text-lg font-semibold">
              {domainInfo.responseTime ? `${domainInfo.responseTime}ms` : 'N/A'}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-2">
              <Server className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">IP Address</span>
            </div>
            <p className="text-lg font-semibold font-mono">
              {domainInfo.ipAddress || 'N/A'}
            </p>
          </div>
        </div>

        {/* SSL Certificate */}
        {domainInfo.ssl && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-green-500" />
                <h3 className="text-lg font-semibold">SSL Certificate</h3>
              </div>
              {domainInfo.ssl.isValid && (
                <span className={`text-sm font-medium ${getSslStatusColor(domainInfo.ssl.daysRemaining)}`}>
                  {domainInfo.ssl.daysRemaining && domainInfo.ssl.daysRemaining > 0
                    ? `${domainInfo.ssl.daysRemaining} days remaining`
                    : 'Expired'}
                </span>
              )}
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Issuer</p>
                <p className="font-medium">{domainInfo.ssl.issuer || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Valid Until</p>
                <p className="font-medium">{formatDate(domainInfo.ssl.validTo)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Quick Metadata Preview */}
        {domainInfo.metadata && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2 mb-4">
              <FileText className="h-5 w-5 text-blue-500" />
              <h3 className="text-lg font-semibold">Page Information</h3>
            </div>
            <div className="space-y-3">
              {domainInfo.metadata.title && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Title</p>
                  <p className="font-medium">{domainInfo.metadata.title}</p>
                </div>
              )}
              {domainInfo.metadata.description && (
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Description</p>
                  <p className="text-sm">{domainInfo.metadata.description}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderDnsTab = () => {
    if (!domainInfo?.dns) return <p className="text-gray-500">No DNS information available</p>;

    const { dns } = domainInfo;

    return (
      <div className="space-y-6">
        {/* A Records */}
        {dns.aRecords && dns.aRecords.length > 0 && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-3">A Records (IPv4)</h3>
            <div className="space-y-2">
              {dns.aRecords.map((record, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Server className="h-4 w-4 text-gray-400" />
                  <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{record}</code>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* MX Records */}
        {dns.mxRecords && dns.mxRecords.length > 0 && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-3">MX Records (Mail)</h3>
            <div className="space-y-2">
              {dns.mxRecords.map((record, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">Priority {record.priority}:</span>
                  <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{record.exchange}</code>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* NS Records */}
        {dns.nsRecords && dns.nsRecords.length > 0 && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-3">NS Records (Nameservers)</h3>
            <div className="space-y-2">
              {dns.nsRecords.map((record, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <Cloud className="h-4 w-4 text-gray-400" />
                  <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{record}</code>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TXT Records */}
        {dns.txtRecords && dns.txtRecords.length > 0 && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-3">TXT Records</h3>
            <div className="space-y-2">
              {dns.txtRecords.map((record, index) => (
                <div key={index} className="bg-gray-50 dark:bg-gray-900 p-3 rounded">
                  <code className="text-xs break-all">{record}</code>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMetadataTab = () => {
    if (!domainInfo?.metadata) return <p className="text-gray-500">No metadata information available</p>;

    const { metadata } = domainInfo;

    return (
      <div className="space-y-6">
        {/* Basic Meta Tags */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold mb-4">Basic Meta Tags</h3>
          <div className="space-y-4">
            {metadata.title && (
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Title</p>
                <p className="mt-1">{metadata.title}</p>
              </div>
            )}
            {metadata.description && (
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Description</p>
                <p className="mt-1 text-sm">{metadata.description}</p>
              </div>
            )}
            {metadata.keywords && (
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Keywords</p>
                <p className="mt-1 text-sm">{metadata.keywords}</p>
              </div>
            )}
            {metadata.author && (
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Author</p>
                <p className="mt-1">{metadata.author}</p>
              </div>
            )}
          </div>
        </div>

        {/* Open Graph Tags */}
        {(metadata.ogTitle || metadata.ogDescription || metadata.ogImage) && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4">Open Graph Tags</h3>
            <div className="space-y-4">
              {metadata.ogTitle && (
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">OG Title</p>
                  <p className="mt-1">{metadata.ogTitle}</p>
                </div>
              )}
              {metadata.ogDescription && (
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">OG Description</p>
                  <p className="mt-1 text-sm">{metadata.ogDescription}</p>
                </div>
              )}
              {metadata.ogImage && (
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">OG Image</p>
                  <img 
                    src={metadata.ogImage} 
                    alt="OG Preview" 
                    className="rounded-lg border border-gray-200 dark:border-gray-700 max-w-xs"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <p className="mt-2 text-xs text-gray-500 break-all">{metadata.ogImage}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Technical Meta Tags */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold mb-4">Technical Meta Tags</h3>
          <div className="grid gap-4 md:grid-cols-2">
            {metadata.viewport && (
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Viewport</p>
                <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{metadata.viewport}</code>
              </div>
            )}
            {metadata.robots && (
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Robots</p>
                <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{metadata.robots}</code>
              </div>
            )}
            {metadata.language && (
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Language</p>
                <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{metadata.language}</code>
              </div>
            )}
            {metadata.generator && (
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Generator</p>
                <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">{metadata.generator}</code>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTechTab = () => {
    if (!domainInfo?.technologies) return <p className="text-gray-500">No technology information available</p>;

    const { technologies } = domainInfo;

    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold mb-4">Detected Technologies</h3>
          <div className="space-y-4">
            {technologies.cms && (
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">CMS</p>
                <p className="mt-1 font-medium">{technologies.cms}</p>
              </div>
            )}
            {technologies.webServer && (
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Web Server</p>
                <p className="mt-1 font-medium">{technologies.webServer}</p>
              </div>
            )}
            {technologies.hosting && (
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Hosting Provider</p>
                <p className="mt-1 font-medium">{technologies.hosting}</p>
              </div>
            )}
            {technologies.jsFrameworks && technologies.jsFrameworks.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">JavaScript Frameworks</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {technologies.jsFrameworks.map((framework, index) => (
                    <span key={index} className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-sm rounded">
                      {framework}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {technologies.analytics && technologies.analytics.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Analytics</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {technologies.analytics.map((tool, index) => (
                    <span key={index} className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 text-sm rounded">
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {technologies.cdns && technologies.cdns.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">CDNs</p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {technologies.cdns.map((cdn, index) => (
                    <span key={index} className="px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 text-sm rounded">
                      {cdn}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderHeadersTab = () => {
    if (!domainInfo?.headers) return <p className="text-gray-500">No headers information available</p>;

    const { headers } = domainInfo;

    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold mb-4">Response Headers</h3>
        <div className="space-y-2">
          {Object.entries(headers).map(([key, value]) => (
            <div key={key} className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
              <div className="font-medium text-sm text-gray-600 dark:text-gray-400">{key}</div>
              <div className="col-span-2 text-sm break-all">{value}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderContentTab = () => {
    if (!domainInfo?.content) return <p className="text-gray-500">No content information available</p>;

    const { content } = domainInfo;

    return (
      <div className="space-y-6">
        {/* Text Content Statistics */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold mb-4">Content Statistics</h3>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Word Count</p>
              <p className="text-2xl font-bold">{content.textContent.wordCount}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Paragraphs</p>
              <p className="text-2xl font-bold">{content.textContent.paragraphs}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Images</p>
              <p className="text-2xl font-bold">{content.images.length}</p>
            </div>
          </div>
          {content.textContent.mainContent && (
            <div className="mt-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Content Preview</p>
              <p className="text-sm bg-gray-50 dark:bg-gray-900 p-3 rounded">{content.textContent.mainContent}</p>
            </div>
          )}
        </div>

        {/* Headings Structure */}
        {Object.keys(content.headings).length > 0 && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4">Heading Structure</h3>
            <div className="space-y-3">
              {Object.entries(content.headings).map(([tag, headings]) => (
                <div key={tag}>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase mb-1">{tag}</p>
                  <ul className="space-y-1">
                    {headings.map((heading, index) => (
                      <li key={index} className="text-sm pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                        {heading}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Images */}
        {content.images.length > 0 && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4">Images ({content.images.length})</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {content.images.map((img, index) => (
                <div key={index} className="flex items-center space-x-2 text-sm">
                  <Image className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="truncate">{img.src}</p>
                    {img.alt && <p className="text-xs text-gray-500">Alt: {img.alt}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Links */}
        <div className="grid gap-6 md:grid-cols-2">
          {content.links.internal.length > 0 && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold mb-4">Internal Links ({content.links.internal.length})</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {content.links.internal.slice(0, 10).map((link, index) => (
                  <div key={index} className="text-sm">
                    <p className="font-medium truncate">{link.text || 'No text'}</p>
                    <p className="text-xs text-gray-500 truncate">{link.href}</p>
                  </div>
                ))}
                {content.links.internal.length > 10 && (
                  <p className="text-xs text-gray-500 italic">+{content.links.internal.length - 10} more</p>
                )}
              </div>
            </div>
          )}

          {content.links.external.length > 0 && (
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold mb-4">External Links ({content.links.external.length})</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {content.links.external.slice(0, 10).map((link, index) => (
                  <div key={index} className="text-sm">
                    <p className="font-medium truncate">{link.text || 'No text'}</p>
                    <p className="text-xs text-gray-500 truncate">{link.href}</p>
                  </div>
                ))}
                {content.links.external.length > 10 && (
                  <p className="text-xs text-gray-500 italic">+{content.links.external.length - 10} more</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Forms */}
        {content.forms.length > 0 && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4">Forms ({content.forms.length})</h3>
            <div className="space-y-4">
              {content.forms.map((form, index) => (
                <div key={index} className="border-l-2 border-blue-500 pl-4">
                  <p className="text-sm font-medium">Form {index + 1}</p>
                  <p className="text-xs text-gray-500">Method: {form.method.toUpperCase()}</p>
                  {form.action && <p className="text-xs text-gray-500">Action: {form.action}</p>}
                  <div className="mt-2">
                    <p className="text-xs font-medium text-gray-600">Inputs:</p>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {form.inputs.map((input, i) => (
                        <span key={i} className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                          {input.type}{input.name && `: ${input.name}`}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact Information */}
        {((content.contactInfo.emails?.length || 0) > 0 || (content.contactInfo.phones?.length || 0) > 0) && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4">Contact Information</h3>
            <div className="space-y-3">
              {content.contactInfo.emails && content.contactInfo.emails.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Email Addresses</p>
                  <div className="space-y-1">
                    {content.contactInfo.emails.map((email, index) => (
                      <p key={index} className="text-sm font-mono">{email}</p>
                    ))}
                  </div>
                </div>
              )}
              {content.contactInfo.phones && content.contactInfo.phones.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Phone Numbers</p>
                  <div className="space-y-1">
                    {content.contactInfo.phones.map((phone, index) => (
                      <p key={index} className="text-sm font-mono">{phone}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Social Media */}
        {content.socialMedia.length > 0 && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold mb-4">Social Media</h3>
            <div className="space-y-3">
              {content.socialMedia.map((social, index) => (
                <div key={index}>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{social.platform}</p>
                  <div className="space-y-1 mt-1">
                    {social.links.map((link, i) => (
                      <p key={i} className="text-xs truncate">{link}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">DNS Intelligent Record Creation</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Enter any domain to get AI-powered DNS record recommendations based on comprehensive website analysis.
        </p>
      </div>

      <div className="card-modern p-6">
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <Globe className="h-5 w-5 text-blue-500" />
            <h2 className="text-lg font-semibold">Domain Analysis</h2>
          </div>
          
          <div className="flex space-x-3">
            <input
              type="text"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter domain (e.g., example.com)"
              className="input-modern flex-1 px-4 py-2 text-sm"
              disabled={isLoading}
            />
            <button
              onClick={handleFetchDomainInfo}
              disabled={!domainInput.trim() || isLoading}
              className="btn-primary px-6 py-2 rounded-lg font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              <span>{isLoading ? 'Analyzing...' : 'Analyze'}</span>
            </button>
          </div>

          <p className="text-xs text-gray-500">
            You can enter the domain with or without protocol (http/https) or www prefix
          </p>
        </div>
      </div>

      {/* Loading Animation */}
      {isLoading && (
        <div className="card-modern p-8">
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-xl font-semibold mb-2">Analyzing {extractDomain(domainInput)}</h3>
              <p className="text-sm text-gray-500">This may take a few moments...</p>
            </div>
            
            <div className="space-y-4 max-w-md mx-auto">
              {loadingSteps.map((step, index) => {
                const Icon = step.icon;
                const isActive = index === loadingStep;
                const isComplete = index < loadingStep;
                
                return (
                  <div
                    key={index}
                    className={`flex items-center space-x-4 transition-all duration-500 ${
                      isActive ? 'scale-105' : ''
                    }`}
                  >
                    <div className={`
                      flex items-center justify-center w-10 h-10 rounded-full transition-all duration-500
                      ${isComplete ? 'bg-green-100 dark:bg-green-900/30' : 
                        isActive ? `bg-${step.color}-100 dark:bg-${step.color}-900/30` : 
                        'bg-gray-100 dark:bg-gray-800'}
                    `}>
                      {isComplete ? (
                        <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                      ) : (
                        <Icon className={`h-5 w-5 transition-all duration-500 ${
                          isActive ? `text-${step.color}-600 dark:text-${step.color}-400 animate-pulse` : 
                          'text-gray-400'
                        }`} />
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <p className={`text-sm font-medium transition-all duration-500 ${
                        isActive ? 'text-gray-900 dark:text-gray-100' : 
                        isComplete ? 'text-green-600 dark:text-green-400' : 
                        'text-gray-400'
                      }`}>
                        {step.text}
                      </p>
                      {isActive && (
                        <div className="mt-1 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div className={`h-full bg-${step.color}-500 rounded-full animate-progress`}></div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="text-center mt-8">
              <div className="inline-flex items-center space-x-2 text-sm text-gray-500">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>Processing advanced AI analysis...</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        </div>
      )}

      {domainInfo && (
        <div className="space-y-8">
          {/* Domain Header */}
          <div className="card-modern p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Globe className="h-6 w-6 text-blue-500" />
                <div>
                  <h2 className="text-xl font-semibold">{domainInfo.domain}</h2>
                  {domainInfo.finalUrl && (
                    <p className="text-sm text-green-600 flex items-center space-x-1">
                      <RefreshCw className="h-3 w-3" />
                      <span>Redirected to: {domainInfo.finalUrl}</span>
                    </p>
                  )}
                  <p className="text-sm text-gray-500">
                    Last analyzed: {new Date(domainInfo.timestamp || Date.now()).toLocaleString()}
                  </p>
                </div>
              </div>
              <a
                href={domainInfo.finalUrl || `https://${domainInfo.domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-1 text-blue-600 hover:text-blue-700"
              >
                <span className="text-sm">Visit Site</span>
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>

          {/* Website Summary - AI Generated */}
          {domainInfo.websiteSummary && (
            <div className="card-modern p-6 bg-gradient-to-br from-blue-50/50 to-purple-50/50 dark:from-blue-950/20 dark:to-purple-950/20 border-2 border-blue-200/50 dark:border-blue-800/50">
              <div className="flex items-center space-x-2 mb-4">
                <Sparkles className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold">AI Business Analysis</h3>
              </div>
              
              <div className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Business Type</span>
                    <p className="font-semibold text-blue-700 dark:text-blue-300">{domainInfo.websiteSummary.businessType}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Industry</span>
                    <p className="font-semibold text-purple-700 dark:text-purple-300">{domainInfo.websiteSummary.industry}</p>
                  </div>
                </div>

                <div>
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Overview</span>
                  <p className="mt-1 text-gray-700 dark:text-gray-300">{domainInfo.websiteSummary.summary}</p>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Target Audience</span>
                    <p className="mt-1">{domainInfo.websiteSummary.targetAudience}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Business Model</span>
                    <p className="mt-1">{domainInfo.websiteSummary.businessModel}</p>
                  </div>
                </div>

                {domainInfo.websiteSummary.keyFeatures.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Key Features</span>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {domainInfo.websiteSummary.keyFeatures.map((feature, index) => (
                        <span key={index} className="px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-sm rounded-full">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {domainInfo.websiteSummary.servicesOrProducts.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Services/Products</span>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {domainInfo.websiteSummary.servicesOrProducts.map((item, index) => (
                        <span key={index} className="px-3 py-1 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 text-sm rounded-full">
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Quick Overview Stats */}
          <div className="grid gap-4 md:grid-cols-4">
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2 mb-2">
                <Globe className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Status</span>
              </div>
              <div className="flex items-center space-x-2">
                {domainInfo.isReachable ? (
                  <CheckCircle className="h-5 w-5 text-green-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500" />
                )}
                <span className={`font-semibold ${domainInfo.isReachable ? 'text-green-600' : 'text-red-600'}`}>
                  {domainInfo.isReachable ? 'Online' : 'Offline'}
                </span>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Response</span>
              </div>
              <p className="text-lg font-semibold">
                {domainInfo.responseTime ? `${domainInfo.responseTime}ms` : 'N/A'}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2 mb-2">
                <Shield className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">SSL</span>
              </div>
              <p className="text-lg font-semibold">
                {domainInfo.ssl?.isValid ? 'Valid' : 'Invalid'}
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-2 mb-2">
                <Server className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium text-gray-600 dark:text-gray-400">IP Address</span>
              </div>
              <p className="text-sm font-semibold font-mono">
                {domainInfo.ipAddress || 'N/A'}
              </p>
            </div>
          </div>

          {/* Intelligent DNS Record Generation - SPOTLIGHT SECTION */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl blur-3xl opacity-10"></div>
            <div className="relative card-modern p-8 border-2 border-blue-500/20">
              <div className="text-center mb-8">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <Sparkles className="h-8 w-8 text-blue-500" />
                  <h2 className="text-2xl font-bold">Intelligent DNS Record Generation</h2>
                  <Sparkles className="h-8 w-8 text-purple-500" />
                </div>
                <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                  AI-powered recommendations based on your website's technologies, services, and content analysis.
                  These records are optimized for security, performance, and deliverability.
                </p>
              </div>
              
              {renderDnsRecommendations()}
            </div>
          </div>

          {/* Additional Analysis Tabs */}
          <div className="card-modern">
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-8 overflow-x-auto px-6 pt-6">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'overview'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('content')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'content'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Content Analysis
                </button>
                <button
                  onClick={() => setActiveTab('dns')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'dns'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Current DNS
                </button>
                <button
                  onClick={() => setActiveTab('metadata')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'metadata'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Metadata
                </button>
                <button
                  onClick={() => setActiveTab('tech')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'tech'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Technologies
                </button>
                <button
                  onClick={() => setActiveTab('headers')}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === 'headers'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Headers
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6 min-h-[400px]">
              {activeTab === 'overview' && renderOverviewTab()}
              {activeTab === 'content' && renderContentTab()}
              {activeTab === 'dns' && renderDnsTab()}
              {activeTab === 'metadata' && renderMetadataTab()}
              {activeTab === 'tech' && renderTechTab()}
              {activeTab === 'headers' && renderHeadersTab()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 